/**
 * @file useExperimentFlagTable.ts
 * @description This file contains the custom hook for all states and functions
 * used by the experiment flag table
 */

import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import * as R from "ramda";
import { useSetError } from "../redux/modals";
import {
  GetFeatureExperimentsResponse,
  ExperimentFlag,
} from "@blisspointmedia/bpm-types/dist/FeatureExperiments";
import { awaitJSON, UsersLambdaFetch } from "../utils/fetch-utils";

type useExperimentFlagTableReturn = any;

export const useExperimentFlagTable = (): useExperimentFlagTableReturn => {
  type FrontendRow = ExperimentFlag & { id: string };

  // Data States
  const [tableData, setTableData] = useState<FrontendRow[]>();
  const [originalData, setOriginalData] = useState<Record<string, FrontendRow>>({});
  const [newExperiments, setNewExperiments] = useState<FrontendRow[]>([]);
  const [updatedExperiments, setUpdatedExperiments] = useState<FrontendRow[]>([]);
  const [deletedExperiments, setDeletedExperiments] = useState<FrontendRow[]>([]);

  // UI States
  const [deletedRows, setDeletedRows] = useState<FrontendRow[]>([]);
  const [showPendingChanges, setShowPendingChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userModalInputErrorMessage, setUserModalInputErrorMessage] = useState<string>("");

  // Use ref for loading state to avoid infinite loops
  const isLoadingDataRef = useRef(false);

  // Set Error Message in UI
  const setError = useSetError();

  // Make sure the page is mounted to avoid leaks
  const isMountedRef = useRef(true);

  // FUNCTIONS

  /**
   * Fetch all data needed from postgres tables
   *
   */
  const getFreshData = useCallback(async () => {
    // Prevent multiple concurrent calls
    if (isLoadingDataRef.current) {
      return;
    }

    isLoadingDataRef.current = true;
    setTableData(undefined);

    try {
      // Fetch all needed data from APIs
      const featureExperimentResponse = await UsersLambdaFetch("/getFeatureExperiments");
      const featureExperimentData: GetFeatureExperimentsResponse = await awaitJSON<GetFeatureExperimentsResponse>(
        featureExperimentResponse
      );

      const experiments = featureExperimentData.experiment_flags.map(row => {
        return {
          ...row,
          id: row.experiment_flag,
        };
      });

      // Create a mapping from a unique ID to each grouped row
      // This is used for the "Discard Changes" Button
      const originalGroupedDataMap: Record<string, FrontendRow & { id: string }> = {};
      if (experiments) {
        for (let experiment of experiments) {
          originalGroupedDataMap[experiment.experiment_flag] = experiment;
        }
      }

      if (isMountedRef.current) {
        setOriginalData(originalGroupedDataMap);
        setTableData(experiments);
        setDeletedRows([]);
      }
    } catch (e) {
      console.error("Error fetching User Experiments data:", e);
      const error: Error = e as Error;
      if (isMountedRef.current) {
        setError({
          message: `Error fetching User Experiments data: ${error.message}`,
          reportError: error,
        });
      }
    } finally {
      if (isMountedRef.current) {
        isLoadingDataRef.current = false;
      }
    }
  }, [setError]);

  // Save function
  const save = useCallback(async () => {
    try {
      // When we update an experiment, we need to delete the original experiment and insert the new one
      const editedDeletes: FrontendRow[] = updatedExperiments.map(
        experiment => originalData[experiment.id]
      );
      const editedInserts: FrontendRow[] = updatedExperiments;

      const allInserts: ExperimentFlag[] = [...newExperiments, ...editedInserts].map(experiment => {
        return {
          experiment_flag: experiment.experiment_flag,
          description: experiment.description,
          created: experiment.created,
          lastmodified: experiment.lastmodified,
          lastuser: experiment.lastuser,
          disable_ui_control: experiment.disable_ui_control,
        };
      });

      const allDeletes: ExperimentFlag[] = [...deletedExperiments, ...editedDeletes].map(
        experiment => {
          return {
            experiment_flag: experiment.experiment_flag,
            description: experiment.description,
            created: experiment.created,
            lastmodified: experiment.lastmodified,
            lastuser: experiment.lastuser,
            disable_ui_control: experiment.disable_ui_control,
          };
        }
      );

      setSaving(true);

      try {
        await UsersLambdaFetch("/updateFeatureExperiments", {
          method: "POST",
          body: {
            experiment_flags: {
              insert: allInserts,
              delete: allDeletes,
            },
          },
        });

        if (isMountedRef.current) {
          await getFreshData();
        }

        setSaving(false);
      } catch (e) {
        console.error("Error during POST of experiment flag data:", e);
        const error: Error = e as Error;
        setSaving(false);
        setError({
          message: `Failed to POST experiment flag data: ${error.message}`,
          reportError: error,
        });
      }
    } catch (e) {
      if (isMountedRef.current) {
        const error: Error = e as Error;
        setSaving(false);
        let message = `Failed to set experiment flag data: ${error.message}`;
        setError({
          message: message,
          reportError: error,
        });
      }
    }
  }, [
    updatedExperiments,
    newExperiments,
    deletedExperiments,
    originalData,
    getFreshData,
    setError,
  ]);

  const clearAllChanges = useCallback(() => {
    if (isMountedRef.current) {
      setTableData(Object.values(originalData ?? {}));
      setDeletedRows([]);
      setNewExperiments([]);
      setDeletedExperiments([]);
    }
  }, [originalData, setTableData, setDeletedRows, setNewExperiments, setDeletedExperiments]);

  const hasPendingChanges: boolean = useMemo(() => {
    const hasPendingChangesResult =
      Boolean(newExperiments.length) ||
      Boolean(updatedExperiments.length) ||
      Boolean(deletedExperiments.length);
    if (!hasPendingChangesResult) {
      setShowPendingChanges(false);
    }
    return hasPendingChangesResult;
  }, [deletedExperiments, newExperiments, updatedExperiments, setShowPendingChanges]);

  // EFFECTS

  // Track component mount/unmount and reset everything
  useEffect(() => {
    isMountedRef.current = true; // Set on mount

    // Reset state and fetch data
    setTableData([]);
    setOriginalData({});
    setDeletedRows([]);
    setNewExperiments([]);
    setDeletedExperiments([]);
    setShowPendingChanges(false);
    setSaving(false);
    setUserModalInputErrorMessage("");
    isLoadingDataRef.current = false;
    getFreshData();

    // Cleanup function runs on unmount
    return () => {
      isMountedRef.current = false;
    };
  }, [getFreshData]);

  // When tableData changes, calculate new and deleted changes
  useEffect(() => {
    if (tableData && originalData) {
      // Find any experiments in tableData whose id isn't in originalData (new)
      const newNewExperiments = R.filter(row => {
        return Boolean(!originalData[row.id]);
      }, tableData);
      setNewExperiments(newNewExperiments);

      // Find any experiments in deletedRows whose id is in originalData (deleted)
      const newDeletedExperiments = R.filter(row => {
        return Boolean(originalData[row.id]);
      }, deletedRows);
      setDeletedExperiments(newDeletedExperiments);

      // Find any experiments in table data that have been edited
      const newUpdatedExperiments = R.filter(row => {
        const { experiment_flag } = row;

        // Potential edited rows are any row in tableData that is in tableData,
        // but is not new and is not marked for deletion
        if (
          originalData[row.id] &&
          !R.find(row => row.id === experiment_flag, deletedRows) &&
          !R.find(row => row.id === experiment_flag, newNewExperiments)
        ) {
          // If you remove the extra metadata added by the table, does the row match
          // the row with the same id in the originalData? If not, this row was edited
          const omitIdIndexSelected = row => R.omit(["id", "index", "selected"], row);
          return !R.equals(
            omitIdIndexSelected(row),
            omitIdIndexSelected(originalData[row.experiment_flag])
          );
        }
        return false;
      }, tableData);

      setUpdatedExperiments(newUpdatedExperiments);
    }
  }, [
    tableData,
    originalData,
    deletedRows,
    setNewExperiments,
    setDeletedExperiments,
    setUpdatedExperiments,
  ]);

  return {
    originalData,
    tableData,
    setTableData,
    newExperiments,
    updatedExperiments,
    deletedExperiments,
    hasPendingChanges,
    showPendingChanges,
    setShowPendingChanges,
    saving,
    save,
    clearAllChanges,
    deletedRows,
    setDeletedRows,
    userModalInputErrorMessage,
    setUserModalInputErrorMessage,
  };
};
